import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';

import type { ScrollToPageOptions } from './pageJump';
import { getPdfPageView } from './pdfPageView';

export function reportPdfPageChange(
  pageNumber: number,
  lastReportedPage: { current: number | null },
  callback: ((pageNumber: number) => void) | undefined
): boolean {
  if (lastReportedPage.current === pageNumber) {
    return false;
  }
  lastReportedPage.current = pageNumber;
  callback?.(pageNumber);
  return true;
}

type UsePdfPageNavigationOptions = {
  initialPage: number;
  status: 'loading' | 'ready' | 'error';
  isHorizontal: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  viewerRef: RefObject<PDFViewer | null>;
  cancelPageJump: () => void;
  startPageJump: (options: ScrollToPageOptions, onCancel: () => void) => void;
  /** Highlight the destination page element after a jump or re-render. */
  onHighlightPage?: (pageNumber: number) => void;
  /** Notify the consumer when the visible page changes (deduplicated). */
  onPageChange?: (pageNumber: number) => void;
};

/**
 * Coordinates page navigation state with pdf.js viewer events. It validates
 * page-number input, tracks the requested and currently visible pages, performs
 * instant initial positioning or smooth user-initiated jumps, and delegates
 * visual highlights and consumer notifications through separate callbacks.
 *
 * Page events:
 * - User scroll: `notifyPageChange` only
 * - Jump lands: `highlightPage` then `notifyPageChange`
 * - Target page re-renders: `highlightPage` only
 *
 * The orchestrator owns the active scroll cancellation handle; this hook
 * requests and cancels jumps through the supplied callbacks.
 */
export function usePdfPageNavigation({
  initialPage,
  status,
  isHorizontal,
  containerRef,
  viewerRef,
  cancelPageJump,
  startPageJump,
  onHighlightPage,
  onPageChange,
}: UsePdfPageNavigationOptions) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const targetPageRef = useRef(initialPage);
  const previousInitialPageRef = useRef(initialPage);
  const pendingTargetJumpRef = useRef(false);
  const isJumpingRef = useRef(false);
  const lastReportedPageRef = useRef<number | null>(null);
  const onHighlightPageRef = useRef(onHighlightPage);
  onHighlightPageRef.current = onHighlightPage;
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const highlightPage = useCallback((pageNumber: number) => {
    onHighlightPageRef.current?.(pageNumber);
  }, []);

  const notifyPageChange = useCallback((pageNumber: number) => {
    reportPdfPageChange(pageNumber, lastReportedPageRef, (reportedPageNumber) =>
      onPageChangeRef.current?.(reportedPageNumber)
    );
  }, []);

  const jumpToPage = useCallback(
    (pageNumber: number, behavior: 'instant' | 'smooth' = 'smooth') => {
      const container = containerRef.current;
      if (!container || !Number.isFinite(pageNumber) || pageNumber < 1) {
        return;
      }

      isJumpingRef.current = true;
      targetPageRef.current = pageNumber;
      setCurrentPage(pageNumber);

      const pageElement = getPdfPageView(viewerRef.current, pageNumber)?.div;

      if (!pageElement) {
        cancelPageJump();
        isJumpingRef.current = false;
        return;
      }

      startPageJump(
        {
          container,
          pageElement,
          horizontal: isHorizontal,
          pageNumber,
          behavior,
          onDone: () => {
            isJumpingRef.current = false;
            // Flash only after the jump finishes so smooth scrolls don't
            // highlight the destination before it is in view.
            highlightPage(pageNumber);
            notifyPageChange(pageNumber);
          },
        },
        () => {
          isJumpingRef.current = false;
        }
      );
    },
    [
      cancelPageJump,
      containerRef,
      highlightPage,
      isHorizontal,
      notifyPageChange,
      startPageJump,
      viewerRef,
    ]
  );

  const handlePagesInit = useCallback(() => {
    jumpToPage(targetPageRef.current, 'instant');
  }, [jumpToPage]);

  const handlePageChanging = useCallback(
    (pageNumber: number) => {
      if (isJumpingRef.current) {
        return;
      }
      setCurrentPage(pageNumber);
      notifyPageChange(pageNumber);
    },
    [notifyPageChange]
  );

  const handlePageRendered = useCallback(
    (pageNumber: number) => {
      if (pageNumber !== targetPageRef.current) {
        return;
      }
      if (pendingTargetJumpRef.current) {
        pendingTargetJumpRef.current = false;
        jumpToPage(pageNumber, 'instant');
      } else {
        highlightPage(pageNumber);
      }
    },
    [highlightPage, jumpToPage]
  );

  const resetNavigation = useCallback(
    (pageNumber: number) => {
      cancelPageJump();
      targetPageRef.current = pageNumber;
      pendingTargetJumpRef.current = true;
      lastReportedPageRef.current = null;
      setCurrentPage(pageNumber);
    },
    [cancelPageJump]
  );

  useEffect(() => {
    const initialPageChanged = previousInitialPageRef.current !== initialPage;
    previousInitialPageRef.current = initialPage;
    targetPageRef.current = initialPage;
    if (initialPageChanged && status === 'ready') {
      jumpToPage(initialPage);
    }
  }, [initialPage, jumpToPage, status]);

  return {
    currentPage,
    jumpToPage,
    handlePagesInit,
    handlePageChanging,
    handlePageRendered,
    resetNavigation,
  };
}
